import { Box, Button, Divider, Edit, Exit, LinkText } from "@bcc-ui/ui-components";
import { useFeature } from "flagged";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps, useSelector } from "react-redux";

import { subpageContext } from "./ApplicationLayout";
import style from "./layout.scss";
import { getFeatureLicense } from "components/licenses/common";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import {
    DELIVERYHISTORY_LICENSES_ROUTE,
    INTEGRATIONS_ROUTE,
    TENANTS_ALL_TENANTS_ROUTE,
} from "components/router/Routes";
import EditTenantView from "components/tenants/edit-tenant/EditTenantView";
import { requestTenantTableFiltersRestore } from "components/tenants/tenants-table/tenantTableFilters";
import tenantStyle from "components/tenants/tenants.scss";
import { FLAG_ASSET_MANAGEMENT } from "services/feature/FeatureFlagService";
import { restoreUrls } from "services/login/endpointRepository";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import {
    getTenantLayerList,
    getTenantName,
    getTenantUuid,
    hasSubTenantCookie,
    hasTenantCookie,
    removeTenantCookie,
    tenantHasFeatureLicense,
    updateTenantName,
    updateTenantUuid,
} from "services/tenants/tenantCookieService";
import { tenantService } from "services/tenants/TenantService";
import { StoreState } from "store";
import { Application } from "store/application";
import { clearAllTenantDetails, popTenantDetails, pushTenantDetails, updateTenantDetails } from "store/tenantDetails";
import { switchTheme } from "store/theme";
import buttons from "styles/buttons.scss";

import testIds from "testIds.json";

interface Result {
    title: string;
    message: string;
    resultVisible: boolean;
    handleCookie?: boolean;
}
const MAP_STATE = (state: StoreState) => {
    const user = state.userReducer.user;
    const username = user === null ? "" : user.username.split("@")[0];
    return { user, username };
};
const MAP_DISPATCH = { popTenantDetails, pushTenantDetails, updateTenantDetails, clearAllTenantDetails, switchTheme };
const CONNECTOR = connect(MAP_STATE, MAP_DISPATCH, null, { forwardRef: true });

const DropTenantAccessBanner = React.forwardRef<HTMLDivElement, ConnectedProps<typeof CONNECTOR>>((props, ref) => {
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const { t } = useTranslation();
    const application = useSelector((state: StoreState) => state.applicationReducer.selected);
    const isAssetManagementEnabled = useFeature(FLAG_ASSET_MANAGEMENT);
    const [result, setResult] = React.useState<Result>({
        title: "",
        message: "",
        resultVisible: false,
        handleCookie: false,
    });
    const [licenceErrorVisible, setLicenceErrorVisible] = React.useState(false);
    const checkFeatureLicense = getFeatureLicense();
    const context = React.useContext(subpageContext);
    const dropAccessHandler = () => {
        props.clearAllTenantDetails();
        props.user &&
            props.pushTenantDetails({
                uuid: props.user.tenantUuid,
                featureLicenses: props.user.featureLicenses,
                region: props.user.region,
                type: props.user.tenantType,
                tenantName: props.username,
                loginMethod: props.user.tenantLoginMethod,
                tenantTier: props.user.tier,
                licensingModel: props.user.licensingModel,
                contractType: props.user.contractType,
                expirationDate: props.user.tenantExpirationDate,
            });
        removeTenantCookie();
        restoreUrls();
        redirectToDefaultView();
    };

    const redirectToDefaultView = () => {
        if (
            application === Application.BAM &&
            tenantHasFeatureLicense("FEATURE_ASSET_MANAGEMENT") &&
            isAssetManagementEnabled
        ) {
            window.location.replace(INTEGRATIONS_ROUTE.path);
        } else {
            requestTenantTableFiltersRestore();
            window.location.replace(TENANTS_ALL_TENANTS_ROUTE.path);
        }
    };

    const dropSubTenant = (tenantUuid: string) => {
        const index = getTenantLayerList().findIndex((item) => {
            return item.uuid === tenantUuid;
        });
        updateTenantUuid(getTenantLayerList()[index].uuid);
        updateTenantName(getTenantLayerList()[index].tenantName);
        const list = getTenantLayerList().splice(index + 1, getTenantLayerList().length - index);
        list.forEach(() => {
            props.popTenantDetails();
        });
        accessTenant(tenantUuid, true);
    };

    const editTenantHandler = () => {
        usageStatisticsService.sendEvent({
            category: Category.TENANT,
            action: Action.EDIT_TENANT,
        });
        if (hasTenantCookie() || hasSubTenantCookie()) {
            const abortController = new AbortController();
            abortControllers.push(abortController);
            context?.setSubpage({
                content: (
                    <div className={style.loaderContainer}>
                        <LoadingIndicator />
                    </div>
                ),
            });
            tenantService
                .fetchTenant(getTenantUuid(), abortController)
                .then((tenantDetails) => {
                    context?.setSubpage({
                        content: (
                            <EditTenantView
                                customerName={tenantDetails.name}
                                status={tenantDetails.status}
                                tier={tenantDetails.tier}
                                uuid={tenantDetails.uuid}
                                tenantRegion={tenantDetails.region}
                                expirationDate={tenantDetails.expirationDate}
                                countryCode={tenantDetails.countryCode}
                                contactName={tenantDetails.contactName}
                                contactEmail={tenantDetails.contactEmail}
                                creationDate={tenantDetails.createdTimestamp}
                                salesforceLeadId={tenantDetails.salesforceLeadId}
                                notes={tenantDetails.notes}
                                salesforceAccountId={tenantDetails.salesforceAccountId}
                                parentExpirationDate={tenantDetails.parentExpirationDate}
                                tenantType={tenantDetails.type}
                                licensingModel={tenantDetails.licensingModel}
                                inTenantAccess={true}
                                updateSubTenant={{ updateCookie, dropAccessHandler }}
                                parentLicensingModel={tenantDetails.parentLicensingModel}
                                parentType={tenantDetails.parentType}
                                parentTier={tenantDetails.parentTier}
                                parentDrawbackPrevention={tenantDetails.parentDrawbackPrevention}
                                salesforceContactEmail={tenantDetails.salesforceContactEmail}
                                salesforceContractState={tenantDetails.salesforceContractState}
                                salesforceContractEndDate={tenantDetails.salesforceContractEndDate}
                                salesforceContractId={tenantDetails.salesforceContractId}
                                drawbackPrevention={tenantDetails.drawbackPrevention}
                                contractType={tenantDetails.contractType}
                                automaticDeletion={tenantDetails.automaticDeletion}
                                parentContractType={tenantDetails.parentContractType}
                                diskQuotaMib={tenantDetails.diskQuotaMib}
                                uploadLimitMib={tenantDetails.uploadLimitMib}
                                downloadLimitMib={tenantDetails.downloadLimitMib}
                                currentRateUuid={tenantDetails.rateUuid}
                            />
                        ),
                        isModal: false,
                    });
                })
                .catch(() => {
                    if (!abortController.signal.aborted) {
                        context?.setSubpage({
                            content: undefined,
                        });
                    }
                });
        }
    };

    const accessTenant = (tenantUuid: string, isPushTenant: boolean) => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        tenantService
            .accessTenant(tenantUuid, abortController)
            .then((data) => {
                if (isPushTenant) {
                    props.updateTenantDetails({
                        uuid: tenantUuid,
                        featureLicenses: data.featureLicenses,
                        region: data.region,
                        type: data.type,
                        tenantName: data.tenantName,
                        loginMethod: data.loginMethod,
                        tenantTier: data.tenantTier,
                        licensingModel: data.licensingModel,
                        parentDrawBackPrevention: data.parentDrawbackPrevention,
                        contractType: data.contractType,
                        expirationDate: data.expirationDate,
                    });
                } else {
                    window.location.reload();
                }
                const checkIfFeatureLicensePresent: boolean = checkFeatureLicense.has(window.location.pathname)
                    ? data.featureLicenses.some((item) => checkFeatureLicense.get(window.location.href)?.includes(item))
                    : true;
                window.location.replace(
                    !checkIfFeatureLicensePresent ? DELIVERYHISTORY_LICENSES_ROUTE.path : window.location.href
                );
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setLicenceErrorVisible(true);
                }
            });
    };

    const updateCookie = () => {
        hideResult();
        const tenantUuid = getTenantUuid();
        accessTenant(tenantUuid, true);
    };

    const hideResult = () => {
        setResult({ title: result.title, message: result.message, resultVisible: false, handleCookie: false });
    };

    const tenantLayerList = getTenantLayerList();

    const displayTenant = (tenantName: string, uuid: string, isCurrentTenant: boolean) => {
        if (isCurrentTenant) {
            return <strong data-testid={testIds.tenantAccess.hierarchy.currentTenant}>{tenantName}</strong>;
        }

        return (
            <React.Fragment key={uuid}>
                <LinkText
                    as={"button"}
                    onClick={() => {
                        props.username == tenantName ? dropAccessHandler() : dropSubTenant(uuid);
                    }}
                    className={style.tenantBannerLinkText}
                    testId={testIds.tenantAccess.hierarchy.dropAccessLink}
                >
                    {tenantName}
                </LinkText>
                {" / "}
            </React.Fragment>
        );
    };

    return (
        <>
            <Box
                verticalAlignment={"Center"}
                horizontalAlignment={"Right"}
                gap={"Md"}
                className={style.tenantBanner}
                data-testid={testIds.tenantAccess.itself}
                ref={ref}
            >
                <div data-testid={testIds.tenantAccess.hierarchy.itself}>
                    {tenantLayerList.map((tenant, index) => {
                        const isCurrentTenant = tenantLayerList.length - 1 === index;
                        return displayTenant(tenant.tenantName, tenant.uuid, isCurrentTenant);
                    })}
                </div>

                <Divider direction={"Vertical"} className={style.tenantBannerDivider} />

                <Button
                    icon={<Edit color={"--app-tenantBannerIconColor"} />}
                    variant={"Tertiary"}
                    onClick={() => editTenantHandler()}
                    testId={testIds.tenantAccess.editTenantButton}
                >
                    {t("Common.edit")}
                </Button>

                <Button
                    icon={<Exit color={"--app-tenantBannerIconColor"} />}
                    variant={"Tertiary"}
                    onClick={() => dropAccessHandler()}
                    testId={testIds.tenantAccess.dropAccessLink}
                >
                    {t("Common.exit")}
                </Button>
            </Box>

            <Modal
                isOpen={result.resultVisible}
                hideModal={result.handleCookie ? updateCookie : hideResult}
                modalTitle={result.title}
            >
                <div className={tenantStyle.resultContainer}>{result.message}</div>
                <div className={tenantStyle.buttonContainer}>
                    <button
                        className={buttons.primaryOkButton}
                        onClick={result.handleCookie ? updateCookie : hideResult}
                        data-testid={testIds.common.confirmationDialog.confirmButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
            <Modal
                isOpen={licenceErrorVisible}
                hideModal={dropAccessHandler}
                modalTitle={t("EditCustomerView.editCustomerLicenceTitle")}
            >
                <div className={tenantStyle.errorContainer}>
                    {t("EditCustomerView.licenseFailureMessage", { customerName: getTenantName() })}
                </div>
                <div className={tenantStyle.buttonContainer}>
                    <button
                        className={buttons.primaryOkButton}
                        onClick={dropAccessHandler}
                        data-testid={testIds.common.confirmationDialog.confirmButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
        </>
    );
});

export default CONNECTOR(DropTenantAccessBanner);
